import React, { useEffect, useState } from 'react';

const TOKEN_KEY = 'analytics_auth_token';
const EXPIRES_AT_KEY = 'analytics_auth_expires_at';
const AUTH_EVENT = 'analytics-auth-changed';

const hasValidStoredToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY));
    return Boolean(token) && Number.isFinite(expiresAt) && expiresAt > Date.now();
  } catch {
    return false;
  }
};

const clearStoredAuth = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
  } catch {
    // noop
  }
  window.dispatchEvent(new Event(AUTH_EVENT));
};

const AnalyticsAuth = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasValidStoredToken()) {
      setIsAuthenticated(true);
    } else {
      clearStoredAuth();
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/auth-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body?.error || 'Invalid password');
      }

      if (!body?.token || !body?.expiresAt) {
        throw new Error('Auth token was not returned by the server');
      }

      localStorage.setItem(TOKEN_KEY, body.token);
      localStorage.setItem(EXPIRES_AT_KEY, String(body.expiresAt));
      window.dispatchEvent(new Event(AUTH_EVENT));

      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Invalid password. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearStoredAuth();
    setIsAuthenticated(false);
    setPassword('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Analytics Dashboard
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              This area is password protected
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Access Dashboard'}
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 text-center bg-yellow-50 p-3 rounded-lg">
                <strong>Development default:</strong> password is "analytics2024" unless server env overrides it.
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={handleLogout}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg"
        >
          Logout
        </button>
      </div>
      {children}
    </div>
  );
};

export default AnalyticsAuth;
